// @flow
import invariant from 'invariant';

import OAuth from './OAuth';
import createDebugger from 'debug';

const debug = createDebugger('oauth:GoogleOAuth');

declare var gapi: any;

interface IGoogleProfile {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  imageUrl: string;
  email: string;
}

type AuthResponse = {|
  expires_at: number,
  expires_in: number,
  first_issue_at: number,
  id_token: string,
  idpId: string,
  login_hint: string,
  token_type: string,
  session_state: any
|};

/**
 * Google OAuth provider
 */
class GoogleOAuth extends OAuth {
  name = 'google';
  defaultClientId =
    '137766908137-ul48bivbmkvt6f2r98id8muqm960vqtb.apps.googleusercontent.com';

  isSDKLoaded() {
    // see public/index.html
    return window.googleAsyncInit.initialized;
  }

  /**
   * Invoked only once to initialize this provider, {@see #initialize}
   */
  async doInitialize(clientId: string) {
    invariant(
      clientId && clientId.indexOf('apps') > -1,
      'The client id is invalid'
    );
    await this.waitForSDK();
    const params = {
      // eslint-disable-next-line camelcase
      client_id: clientId
    };
    debug('google sdk initialized with: ', params);
    await new Promise(resolve => gapi.load('auth2', resolve));
    this.providerInitialized = true;
    this.emit('initialize');
    return gapi.auth2.init(params);
  }

  async getLoginStatus(): Promise<boolean> {
    await this.waitForSDK();
    return gapi.auth2.getAuthInstance().isSignedIn.get();
  }

  isLoggedIn(): Promise<boolean> {
    return this.getLoginStatus();
  }

  async login(): Promise<any> {
    await this.waitForSDK();
    return gapi.auth2.getAuthInstance().signIn();
  }

  async logout(): Promise<any> {
    await this.waitForSDK();
    return gapi.auth2.getAuthInstance().signOut();
  }

  async getToken(): Promise<string> {
    await this.waitForSDK();
    const authResponse: AuthResponse = await gapi.auth2
      .getAuthInstance()
      .currentUser.get()
      .getAuthResponse();
    return authResponse.id_token;
  }

  async getProfile(): Promise<IGoogleProfile> {
    await this.waitForSDK();
    const profile = gapi.auth2
      .getAuthInstance()
      .currentUser.get()
      .getBasicProfile();
    return {
      id: profile.getId(),
      name: profile.getName(),
      firstName: profile.getGivenName(),
      lastName: profile.getFamilyName(),
      imageUrl: profile.getImageUrl(),
      email: profile.getEmail()
    };
  }
}

const provider = new GoogleOAuth();

export default provider;



// WEBPACK FOOTER //
// ./src/utils/oauth/GoogleOAuth.js